/* @flow */
/* eslint-env es6,browser */

import * as u from '../utils';
import bidbase from './bidbase';
import bm from '../bidmanager';
import * as fetch from '../utils/fetch';
import { BrowserUtil } from '../utils/browserutil';

/**
 * @module bidders/pubmatic
 */
/**
 * @global
 * @description extends {@link hbConfig}
 * @property {string} [qpKey=puhb]
 * @property {string} id
 */
/**
 * @description Implements functionality for pubmatic header bidding
 * @exports pubmaticbid
 * @extends _bidbase
 * @requires module:utils
 * @requires module:adloader
 */

class _pubmatic extends bidbase {
  _bidRequest: Object;
  constructor() {
    super('pubmatic');
    this._DefConfig = u.extend(true, {}, this._DefConfig, {
      pubID: '',
      qpKey: 'puhb'
    });
    this._isSupported = u.isJSONSupported() && fetch.isSupported();
    this._config = u.extend(true, {}, this._DefConfig);
    this._mandatoryConfigs = ['bidUrl', 'pubID'];
    this._bidRequest = {
      impid: {}
    };
  }

  /**
   * Process bid response. Response format:
   * @param {Array<string>} posList - HB position list
   * @param {Object} response - Response from server
   * @param {Object.<string, hbPosData>} ret - Return data
   * @return {Object.<string, hbPosData>} - Return data
   * @access private
   */
  _processBidResponse(
    posList: Array<string>,
    response: any,
    ret: hbPartnerData
  ): hbPartnerData {
    if (!this._isValidRTBResponse(response)) {
      return ret;
    }
    response.seatbid[0].bid.forEach(bid => {
      if (bid && bid.impid && bid.price && bid.adm) {
        const impid = bid.impid;
        this._handleBidPosData(
          ret,
          this._bidRequest.impid[impid],
          bid.price,
          impid,
          decodeURIComponent(bid.adm)
        );
      }
    });
    return ret;
  }

  /**
   * Create slot object for an ad position
   * @param {string} pos - Ad position
   * @returns {Object} - Request URL info for given position
   * @access private
   */
  _getSlot(pos: string): Object {
    //get all the required params
    const randid = u.getRandom();
    let sizes = this._config.posTokenMap[pos].sizes.slice();
    let width = 0,
      height = 0;
    if (sizes.length) {
      [width, height] = sizes.shift();
    }
    const fmt = sizes.map(([wt, ht]) => ({ w: wt, h: ht }));
    const params = {
      id: randid,
      tagid: this._config.posTokenMap[pos].adTagId,
      secure: u.isSecure() ? 1 : 0,
      bidfloorcur: 'USD',
      banner: {
        w: width,
        h: height,
        topframe: this._config.posTokenMap[pos].iframe === false ? 1 : 0,
        format: fmt
      }
    };
    this._bidRequest.impid[randid] = pos;
    return params;
  }

  /**
   * Build request params in RTB format
   * @param {Array<Object>} slots - Ad position
   * @returns {Object} - Request URL info for given positions
   * @access private
   */
  _buildReqParams(slots: Array<Object>): Object {
    // build params for bid request
    let params = {
      id: u.getRandom(),
      site: {
        publisher: {
          id: this._config.pubID
        },
        page: window.location.href || '',
        ref: document.referrer || ''
      },
      device: {
        ua: BrowserUtil.getUserAgent(),
        dnt: u.isDNT() ? 1 : 0,
        h: window.screen.height || '',
        w: window.screen.width || '',
        language: navigator.language || ''
      },
      imp: slots,
      at: 1
    };
    return params;
  }

  /**
   * Builds and sends bid requests
   * @param {Array<string>} posList - Ad positions
   * @returns {Promise} - Returns promise
   * @access private
   */
  _getBids(posList: Array<string>): Promise<*> {
    const slots = posList.map(pos => this._getSlot(pos));
    const opt = {
      data: {},
      timeout: this._config.timeout,
      logPrefix: `${this.getName()} - ${posList.join(',')}`
    };
    const req = {
      method: 'POST',
      body: JSON.stringify(this._buildReqParams(slots)),
      credentials: 'include'
    };
    return fetch.JSON(this._config.bidUrl, opt, req).then(json => json);
  }

  /**
   * This method will be called for bidders witout overriden userSync method
   * @returns {Array<syncObj>} - Array of sync objects
   * @access private
   */
  _getUserSync(): Array<syncObj> {
    let ret = [];
    //make sure that syncUrl & pubID is not empty
    if (this._config.syncUrl.length && this._config.pubID.length) {
      ret.push({
        name: this.getName(),
        type: 'iframe',
        url: `${this._config.syncUrl}${this._config.pubID}`
      });
    }
    return ret;
  }

  /**
   * get bids for given positions
   * @param {Array<string>} posList - HB Position list
   * @param {Object.<string, hbPosData>} ret - Return data
   * @return {Promise<Object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    this._bidResponses = {};
    this._bidRequest.impid = {};
    return this._getBids(posList).then(
      response => this._processBidResponse(posList, response, ret),
      error => {
        u.logError(`${this._name} Failed to get bids: ${error || ''}`);
        return ret;
      }
    );
  }
}
//add bidder to bidmanager
const pubmatic = new _pubmatic();
bm.addBidder('pubmatic', pubmatic);
export default pubmatic;
